import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import styled from 'styled-components';
import _toUpper from 'lodash/toUpper';
import _size from 'lodash/size';
import _trim from 'lodash/trim';
import _replace from 'lodash/replace';
import _isEmpty from 'lodash/isEmpty';
import { STRINGS } from 'config';
import { OkCircle } from 'assets/primaryCare/icons';
import { ColumnDiv } from 'styles/elements/div';
import Question from 'styles/blocks/question';
import { Error } from 'styles/blocks/error';
import InputElement from 'styles/elements/input';
import { consultationSelector } from 'redux/selectors/consultation';
import { updateFreeTextAnswer, validatePostcode } from 'redux/actions/consultation/answer';
import { useAutoFocus } from 'common/customHooks';
import { NextButton } from '../nextButton';
import { createMarkup } from '../compound/compoundBody';

const PostcodeContainer = styled(ColumnDiv)`
  width: 100%;
`;

const PostcodeInner = styled(ColumnDiv)`
  min-height: 80px;
  position: relative;
  width: 50%;
`;

const PostcodeError = styled(Error)`
  margin-bottom: 10px;
  margin-top: 0;
`;

const PostcodeInput = styled(InputElement)`
  position: absolute;
  width: 100%;
  left: 0;
`;

const PostcodeOkIcon = styled(OkCircle)`
  height: 45px;
  width: 45px;
  position: absolute;
  left: 100%;
  transform: translateX(-65px);
  top: 17px;
  svg{
    height: 24px;
    width: 24px;
  }
`;

/**
 * Simple freeTextShort component that validates postcode input without doing the full address lookup functionality
 * of the address.postcode component
 * NB Styled for eTriage use
 */
export function Postcode(props) {
  const { question, isMounted, onClickNextQuestion, isGuidanceTextShownAboveQuestion } = props;
  const mapState = useCallback(state => consultationSelector(state), []);
  const {
    freeTextAnswer,
    inputsReadOnly,
    postcodeAPIResults,
    postcodeValidated,
    validationErrors,
  } = useMappedState(mapState);
  const dispatch = useDispatch();

  const inputRef = useRef();
  useAutoFocus(true, isMounted, inputRef);

  const [answerText, setAnswerText] = useState(freeTextAnswer);
  const [isPostCodeInvalid, setIsPostCodeInvalid] = useState(false);
  const [isPostcodeOk, setIsPostcodeOk] = useState(postcodeValidated);
  const [isInitialState, setIsInitialState] = useState(true);

  const containsNonPostcodeChars = postcode => !postcode?.match(/^[a-zA-Z0-9\s-]*$/);

  const updatePostcodeInState = postcode => {
    setIsInitialState(false);
    if (containsNonPostcodeChars(postcode)) {
      return setIsPostcodeOk(false);
    }
    const upperPostcode = _toUpper(postcode);
    setAnswerText(upperPostcode);
    if (_size(upperPostcode) > 3) {
      dispatch(validatePostcode(upperPostcode));
    } else {
      setIsPostcodeOk(false);
      // call postcode success action to reset API results to empty set
      dispatch({ type: STRINGS.CONSULTATION_REDUCER.VALIDATE_POSTCODE_SUCCESS, payload: [] });
    }
    return dispatch(updateFreeTextAnswer(upperPostcode));
  };

  const checkPostcodeValid = () => {
    const trimmedPostcode = _trim(answerText);
    const postcodeOk = _size(trimmedPostcode) > 3 && isPostcodeOk;
    setIsPostcodeOk(postcodeOk);
    setIsPostCodeInvalid(!postcodeOk);
    return postcodeOk;
  };

  const handleSubmit = () => {
    if (!checkPostcodeValid()) {
      return false;
    }
    return onClickNextQuestion();
  };

  useEffect(() => {
    if (!(isInitialState && isPostcodeOk && _size(freeTextAnswer) > 0)) {
      if (!postcodeAPIResults || _size(postcodeAPIResults) !== 1) {
        setIsPostcodeOk(false);
      } else {
        // apply the api postcode format / spacing & mark valid, but only if the trimmed api results match the trimmed entered text
        const trimmedAnswerText = _replace(answerText, /\s/g, '');
        const trimmedApiPostcode = _replace(postcodeAPIResults[0], /\s/g, '');
        if (trimmedAnswerText === trimmedApiPostcode) {
          setIsPostcodeOk(true);
          setIsPostCodeInvalid(false);
          setAnswerText(postcodeAPIResults[0]);
          dispatch(updateFreeTextAnswer(postcodeAPIResults[0]));
        } else {
          setIsPostcodeOk(false);
        }
      }
    }
  }, [postcodeAPIResults, isPostcodeOk, isInitialState, answerText, freeTextAnswer, dispatch]);

  const isPostcodeInAPIValidationErrors = !_isEmpty(validationErrors);

  return (
    <PostcodeContainer>
      <PostcodeContainer>
        {isPostCodeInvalid && <PostcodeError>Please enter a valid postcode to proceed</PostcodeError>}
      </PostcodeContainer>
      <PostcodeContainer>
        <PostcodeInner>
          <PostcodeInput
            value={answerText}
            type={STRINGS.INPUT_TYPES.TEXT}
            isInvalid={isPostCodeInvalid || isPostcodeInAPIValidationErrors}
            onChange={event => updatePostcodeInState(event.target.value)}
            onBlur={() => checkPostcodeValid()}
            aria-label="Your postcode"
            maxLength="8"
            readOnly={inputsReadOnly}
            id="postCode-input"
            data-automation="postCode-input"
          />
          {isPostcodeOk && <PostcodeOkIcon />}
        </PostcodeInner>
      </PostcodeContainer>
      <PostcodeContainer>
        {question.guidanceScript && !isGuidanceTextShownAboveQuestion && <Question.ETriageHelpLabel dangerouslySetInnerHTML={createMarkup(question.guidanceScript)} />}
      </PostcodeContainer>
      <NextButton onClick={handleSubmit} isDisabled={!isPostcodeOk} dataAutomation={`nextButton-${question.questionId}`} />
    </PostcodeContainer>
  );
}
